import { Injectable } from '@angular/core';

import { gateway } from 'libs/generated-grpc-web';

import { downloadjs } from '@app/src/app/util/downloadjs';

import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';

import { CalendarExportInput } from './calendar-export.types';

/**
 * Calendar service for Calendar-PhoneGap-Plugin
 * https://github.com/LeumitDev/Calendar-PhoneGap-Plugin
 */
@Injectable({ providedIn: 'root' })
export class CalendarExportService {
  private _calendarPlugin: any;
  private readonly _isMobileDevice: boolean = window.MINGA_APP_ANDROID || window.MINGA_APP_IOS;

  constructor(private _systemAlertSnackBar: SystemAlertSnackBarService) {
    if (this._isMobileDevice) {
      const plugins = (<any>window).plugins;
      this._calendarPlugin = plugins.calendar;
    }
  }

  private _removeHTML(value: string) {
    const div = document.createElement('div');
    div.innerHTML = value;
    return div.textContent || '';
  }

  async addLongEventContentToCalendar(event: gateway.content_views_pb.LongEventCardView.AsObject, hash: string) {
    if (!this._isMobileDevice) {
      await this._addToCalendarWeb(event, hash);
    } else {
      await this._addToCalendarMobile(event);
    }
  }

  private async _addToCalendarMobile(event: gateway.content_views_pb.LongEventCardView.AsObject) {
    if (event?.startTimestamp) {
      const startDate = new Date(event.startTimestamp);
      const endDate = new Date(event.endTimestamp);
      const title = event.title;
      const location = event.location;
      const notes = this._removeHTML(event.fullBody);

      try {
        const calOptions = this._calendarPlugin.getCalendarOptions();
        // add ticket url for event url if applicable
        if (event.ticketUrl) {
          calOptions.url = event.ticketUrl;
        }
        const existingEvent = await new Promise((resolve, reject) => {
          this._calendarPlugin.findEventWithOptions(
            title,
            location,
            notes,
            startDate,
            endDate,
            calOptions,
            (message: any[]) => {
              const hasEvent = !!message && message.length;
              resolve(hasEvent);
            },
            (message: any) => {
              console.error(`[MobileCalendarService] addLongEventContentToCalendar() find event failed: `, message);
              reject(false);
            },
          );
        });

        if (existingEvent) {
          this._systemAlertSnackBar.warning(`Event "${title}" already added to your calendar!`);
          return;
        }

        this._calendarPlugin.createEventInteractivelyWithOptions(
          title,
          location,
          notes,
          startDate,
          endDate,
          calOptions,
          (message: any) => {
            // No message needed as the user will have chosen in their native
            // calendar app how to save the event
          },
          (message: any) => {
            console.error(`[MobileCalendarService] addLongEventContentToCalendar() failed: `, message);
            this._systemAlertSnackBar.error(`An error occured, please try again.`);
          },
        );
      } catch (err) {
        console.error(`[MobileCalendarService] addLongEventContentToCalendar() failed: `, err);
        this._systemAlertSnackBar.error(`An error occured, please try again.`);
      }
    } else {
      this._systemAlertSnackBar.error(`Cannot add event to Calendar, missing start date & time.`);
    }
  }

  private async _addToCalendarWeb(event: gateway.content_views_pb.LongEventCardView.AsObject, hash: string) {
    if (event?.startTimestamp) {
      const startDate = new Date(event.startTimestamp);
      const endDate = new Date(event.endTimestamp);
      const title = event.title;
      const notes = this._removeHTML(event.fullBody);

      try {
        const calExport: CalendarExportInput = {
          start: {
            dateICS: this._formatDateToICS(startDate),
          },
          end: {
            dateICS: this._formatDateToICS(endDate),
          },
          summary: title,
          description: notes,
          url: event.ticketUrl,
          stampICS: this._formatDateToICS(startDate),
          sequence: 1,
          uid: hash,
        };

        const ics = this._convertToIcs(calExport);

        downloadjs(ics, 'minga_event-' + hash + '.ics');
      } catch (e) {
        this._systemAlertSnackBar.error(`An error occured, please try again.`);
      }
    }
  }

  // Format date to YYYYMMDDTHHMMSSZ format required by ics format
  private _formatDateToICS(date: Date) {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  }

  private _convertToIcs(event: CalendarExportInput): string {
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'PRODID:-//Minga Solutions//Minga//EN',
      'BEGIN:VEVENT',
      `UID:${event.uid}`,
      `SEQUENCE:${event.sequence}`,
      `DTSTAMP:${event.start.dateICS}`,
      `DTSTART:${event.start.dateICS}`,
      `DTEND:${event.end.dateICS}`,
      `SUMMARY:${event.summary}`,
      `DESCRIPTION:${event.description}`,
      `URL:${event.url}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
  }
}
